package nl.volleyplanner.web.base.component

import kotlinx.browser.document
import kotlinx.coroutines.flow.StateFlow
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.onClickFunction
import kotlinx.html.org.w3c.dom.events.Event
import nl.volleyplanner.web.base.SimpleComponent
import nl.volleyplanner.web.base.StandaloneHTMLComponent
import nl.volleyplanner.web.base.simpleComponent
import nl.volleyplanner.web.lib.Modal
import org.w3c.dom.HTMLElement

enum class ModalSize(val cssClass: String?) {
    // 300px
    Small("modal-sm"),
    // 500px - default
    Medium(null),
    // 800px
    Large("modal-lg"),
    // 1140px
    ExtraLarge("modal-xl")
}

abstract class ModalComponent(
    val modalTitle: String,
    val modalSize: ModalSize = ModalSize.Medium
) : StandaloneHTMLComponent() {
    open val footerButtons: List<BootstrapButton> = emptyList()

    private val modalElement = document.create.div (classes = "modal") {
        tabIndex = "-1"
        div (classes = "modal-dialog") {
            if (modalSize.cssClass != null) classes += modalSize.cssClass

            div(classes = "modal-content") {
                div(classes = "modal-header") {
                    h5(classes = "modal-title") { +modalTitle }
                    button(classes = "btn-close") {
                        type = ButtonType.button
                        attributes["data-bs-dismiss"] = "modal"
                        attributes["aria-label"] = "Close"
                    }
                }
                div(classes = "modal-body") {

                }
                div(classes = "modal-footer") {

                }
            }
        }
    }

    private var bootstrapModal: Modal? = null

    override fun initialize() {
        bootstrapModal = Modal.getOrCreateInstance(modalElement)
    }

    override suspend fun getState(): StateFlow<*>? = null

    override fun onDispose() {
        super.onDispose()
        bootstrapModal?.hide()
        bootstrapModal?.dispose()
        modalElement.parentNode?.removeChild(modalElement)
    }

    fun show() = bootstrapModal?.show() ?: throw IllegalStateException("Offcanvas not initialized")

    fun hide() = bootstrapModal?.hide() ?: throw IllegalStateException("Offcanvas not initialized")

    final override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val bodyElement = modalElement.querySelector(".modal-body")!!
        val footerElement = modalElement.querySelector(".modal-footer")!!

        val newBodyElement = consumer.div(classes = "modal-body") { renderBody() }
        bodyElement.replaceWith(newBodyElement)

        val newFooterElement = consumer.div(classes = "modal-footer") {
            footerButtons.forEach { bootstrapButton ->
                simpleComponent(bootstrapButton)
            }
        }
        footerElement.replaceWith(newFooterElement)

        return modalElement
    }

    abstract fun FlowContent.renderBody()
}

class OkCancelModalComponent(
    modalTitle: String,
    val modalBody: FlowContent.() -> Unit,
    private val onOk: () -> Unit
) : ModalComponent(modalTitle, ModalSize.Medium) {
    override val footerButtons = listOf(
        BootstrapButton("Ok", ButtonVariant.Primary, buttonOnClickFunction = { onOk(); hide() }),
        BootstrapButton("Cancel", ButtonVariant.Secondary, buttonOnClickFunction = { hide() })
    )

    override fun FlowContent.renderBody() {
        div { modalBody() }
    }
}

enum class ButtonVariant(val cssClass: String) {
    Primary("btn-primary"),
    Secondary("btn-secondary"),
    Success("btn-success"),
    Danger("btn-danger"),
    Warning("btn-warning"),
    Info("btn-info"),
    Light("btn-light"),
    Dark("btn-dark")
}

data class BootstrapButton(
    val buttonLabel: String,
    val buttonVariant: ButtonVariant,
    val buttonOnClickFunction: ((Event) -> Unit)? = null,
    val attributeMap: Map<String, String> = emptyMap()
) : SimpleComponent {
    companion object {
        fun primaryButton(buttonLabel: String, onClickFunction: (Event) -> Unit) = BootstrapButton(buttonLabel, ButtonVariant.Primary, onClickFunction)
        fun secondaryButton(buttonLabel: String, onClickFunction: (Event) -> Unit) = BootstrapButton(buttonLabel, ButtonVariant.Secondary, onClickFunction)
    }

    override fun render(flowContent: FlowContent) {
        flowContent.button (classes = "btn ${buttonVariant.cssClass}") {
            type = ButtonType.button
            attributes.putAll(attributeMap)
            if (buttonOnClickFunction != null) {
                onClickFunction = buttonOnClickFunction
            }
            +buttonLabel
        }
    }
}
